import { ChangeEvent } from 'react'
import {
  Button,
  Flex,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalProps,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Text,
} from '@ritualco/jutsu'
import 'react-multi-email/style.css'
import * as Yup from 'yup'
import { useFormikContext, Form, Formik, FormikOnSubmit } from 'formik'
import { MerchantList, MerchantParticipation } from 'types/entities'
import { HeraModal } from 'components/HeraModal'
import { useContent } from 'lib/content/react'

const DISTANCES = [1, 2, 3, 5, 10, 15]

export type MerchantParticipationModalProps = {
  merchantParticipation?: MerchantParticipation
  merchantLists?: MerchantList[]
  isOpen: boolean
  onClose: () => void
  onSubmit: FormikOnSubmit<MerchantParticipation>
} & Omit<ModalProps, 'children'>

// todo: replace hardcoded string with Contentful strings
export const MerchantParticipationModal = ({
  merchantParticipation,
  merchantLists = [],
  onClose,
  onSubmit,
  ...modalProps
}: MerchantParticipationModalProps) => {
  const { formatMessage } = useContent()
  const FormValueTypeSchema = Yup.object().shape({
    type: Yup.string(),
    merchantList: Yup.string().when('type', {
      is: (value: string) => value === 'merchant_list',
      then: Yup.string().required('Select a merchant list'),
    }),
  })
  const initialValues: MerchantParticipation = merchantParticipation || {
    type: 'all_merchants',
  }

  return (
    <HeraModal size="xl" onClose={onClose} {...modalProps}>
      <Formik
        initialValues={initialValues}
        validationSchema={FormValueTypeSchema}
        onSubmit={async (values, actions) => {
          await onSubmit(values, actions)
          actions.setSubmitting(false)
          onClose()
        }}
      >
        {props => (
          <>
            <ModalHeader>Participating restaurants</ModalHeader>
            <ModalBody>
              <MerchantParticipationForm merchantLists={merchantLists} />
            </ModalBody>
            <ModalFooter>
              <Button colorScheme="transparent" mr={2} onClick={onClose}>
                {formatMessage({ id: 'pmportal.global.actionCancel' })}
              </Button>
              <Button
                type="submit"
                form="merchantParticipation"
                isLoading={props.isSubmitting}
              >
                {formatMessage({ id: 'pmportal.global.actionDone' })}
              </Button>
            </ModalFooter>
          </>
        )}
      </Formik>
    </HeraModal>
  )
}

interface MerchantParticipationFormProps {
  merchantLists?: MerchantList[]
}

const MerchantParticipationForm = ({
  merchantLists = [],
}: MerchantParticipationFormProps) => {
  // const { formatMessage } = useContent()
  const {
    setFieldValue,
    errors,
    values,
  } = useFormikContext<MerchantParticipation>()
  const onRadioSelectionChange = (newSelection: string) => {
    setFieldValue('type', newSelection)
    if (newSelection === 'radius') {
      setFieldValue('merchantRadius', DISTANCES[0])
      setFieldValue('merchantRadiusUnit', 'kilometer')
    } else {
      // reset all additional fields
      setFieldValue('merchantRadius', undefined)
      setFieldValue('merchantRadiusUnit', undefined)
      setFieldValue('merchantList', undefined)
    }
  }
  const onMerchantListChange = (e: ChangeEvent<HTMLSelectElement>) =>
    setFieldValue(e.target.name, e.target.value)
  const onMerchantRadiusChange = (e: ChangeEvent<HTMLSelectElement>) => {
    setFieldValue(e.target.name, e.target.value)
    // todo: change when more units are supported
    setFieldValue('merchantRadiusUnit', 'kilometer')
  }

  return (
    <Form id="merchantParticipation">
      <Flex direction="column">
        <Text
          fontSize="small"
          color="grayscale.secondary"
          pt={1}
          mt={-5}
          mb={6}
        >
          Select the restaurants where users may redeem this incentive.
        </Text>
        <RadioGroup
          mt={4}
          name={'type'}
          onChange={onRadioSelectionChange}
          value={values.type}
        >
          <Stack>
            <Flex direction="column" gridGap={6}>
              <Radio value={'all_merchants'}>
                <Text ml={2}>All Ritual Restaurants</Text>
              </Radio>
              <Radio value={'workspace'}>
                <Text ml={2}>Restaurants in my building</Text>
              </Radio>
              <Flex direction="column">
                <Flex alignItems={'center'}>
                  <Radio value={'merchant_list'}>
                    <Text ml={2}>Choose restaurant list</Text>
                  </Radio>
                  <Select
                    name="merchantList"
                    w="unset"
                    ml={4}
                    placeholder="Select List"
                    onChange={onMerchantListChange}
                    disabled={values.type !== 'merchant_list'}
                  >
                    {merchantLists.map((ml: MerchantList, idx: number) => (
                      <option key={idx} value={ml.name}>
                        {ml.name}
                      </option>
                    ))}
                  </Select>
                </Flex>
                {errors.merchantList && (
                  <Text pt={1} fontSize={'sm'}>
                    {errors.merchantList}
                  </Text>
                )}
              </Flex>
              <Flex alignItems={'center'}>
                <Radio value={'radius'}>
                  <Text ml={2}>Within a set distance</Text>
                </Radio>
                <Select
                  name="merchantRadius"
                  w="unset"
                  ml={4}
                  value={DISTANCES[0]}
                  onChange={onMerchantRadiusChange}
                  disabled={values.type !== 'radius'}
                >
                  {DISTANCES.map((d: number) => (
                    <option key={d} value={d}>
                      {d} KM
                    </option>
                  ))}
                </Select>
              </Flex>
            </Flex>
          </Stack>
        </RadioGroup>
      </Flex>
    </Form>
  )
}
